import { backgroundWaitLine, type BackgroundTask } from "@rivets/core";
import type { ChatBlock } from "./foldChatEvents";

/**
 * The words every surface uses for work an agent left running in the
 * background. The native apps mirror these (`BackgroundTaskWording`), so a
 * change here is a change there too.
 */

/** How many running tasks the live indicator names before it just counts. */
export const MAX_LISTED_BACKGROUND_TASKS = 3;

/** The provider's own word, only ever "completed" for the good outcome. */
export function backgroundTaskSucceeded(status: string): boolean {
  return status === "completed";
}

/** "Background task finished", or "Background task failed" / "… killed". */
export function backgroundTaskTitle(status: string): string {
  return backgroundTaskSucceeded(status) ? "Background task finished" : `Background task ${status}`;
}

/**
 * The hint a backgrounded call's own row carries, or null for none.
 *
 * Null once the run settled the row without the task ever reporting back:
 * there is nothing true left to say about how it went.
 */
export function backgroundToolHint(block: Extract<ChatBlock, { kind: "tool" }>): string | null {
  if (!block.background) return null;
  if (!block.backgroundSettled) return "Running in background";
  if (block.backgroundStatus === undefined) return null;
  return backgroundTaskSucceeded(block.backgroundStatus)
    ? "Finished in background"
    : `${capitalize(block.backgroundStatus)} in background`;
}

export interface BackgroundWait {
  /** "Waiting on 2 background tasks". */
  label: string;
  /** The first few running tasks' descriptions, in the provider's order. */
  descriptions: string[];
  /** "and 4 more", or null when every task is already named. */
  overflow: string | null;
}

/** What the live indicator says while a run is held open by background work,
 * or null when nothing is running in the background. */
export function backgroundWait(tasks: readonly BackgroundTask[]): BackgroundWait | null {
  if (tasks.length === 0) return null;
  const hidden = tasks.length - MAX_LISTED_BACKGROUND_TASKS;
  return {
    label: backgroundWaitLine(tasks.length),
    descriptions: tasks.slice(0, MAX_LISTED_BACKGROUND_TASKS).map((task) => task.description),
    overflow: hidden > 0 ? `and ${hidden} more` : null,
  };
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
